import JSZip from 'jszip';
import CryptoJS from 'crypto-js';
import { fetchFileContent, listAllFiles } from "./songs-db";
import { 
  storeBackup as storeBackupInDb, 
  getBackups,
  getBackup,
  deleteBackup,
  deleteOldBackups,
  observeBackupsChanges
} from "./songs-backup-db";
import { handleZippedTextFiles } from "../../../shared/services/zipUtils";
import {assertWorkspaceWritable} from '../../../shared/services/workspaceAccess';
import {getWorkspaceId} from '../../../shared/services/dbUtils';
import {LIBRARY_MANIFEST, readSongLibraryState, validateLibraryState} from './song-library-store';

export class SongBackupService {
  /**
   * Creates a backup of all current songs
   */
  static async createSongsBackup(backupName = null) {
    const zip = new JSZip();
    const files = (await listAllFiles()).filter(path => /\.txt$/i.test(path));
    let totalSize = 0;
    
    // Add each file to the zip archive
    for (const fileName of files) {
      const content = await fetchFileContent(fileName);
      if (content === null || content === undefined) throw new Error(`Nie udało się odczytać pliku ${fileName}`);
      totalSize += new Blob([content]).size;
      zip.file(fileName, content);
    }
    
    // Song metadata travels with the same ZIP; fileCount continues to count TXT files only.
    zip.file(LIBRARY_MANIFEST, JSON.stringify(readSongLibraryState(getWorkspaceId())));

    // Generate filename with timestamp if not provided
    const fileName = backupName || `Chwała_net_kopia_${this.getFormattedDateTime()}.zip`;
    const blob = await zip.generateAsync({type: 'blob'});
    
    // Calculate content hash here as well
    const contentHash = await this.calculateContentHash();

    return { 
      fileName, 
      blob, 
      fileCount: files.length,
      totalFileSize: totalSize,
      zipSize: blob.size,
      contentHash // Include contentHash
    };
  }
  
  /**
   * Gets current datetime formatted as YYYYMMDD_HHMM
   */
  static getFormattedDateTime() {
    const date = new Date();
    const pad = value => String(value).padStart(2, '0');
    const formattedDate = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
    const formattedTime = `${pad(date.getHours())}${pad(date.getMinutes())}`;
    return `${formattedDate}_${formattedTime}`;
  }
  
  /**
   * Calculates a hash of the files and their contents for comparison
   */
  static async calculateContentHash() {
    const files = (await listAllFiles()).filter(path => /\.txt$/i.test(path));
    const hash = CryptoJS.algo.SHA256.create();
    
    // Sort files to ensure consistent hash calculation
    const sortedFiles = [...files].sort();
    
    for (const fileName of sortedFiles) {
      const content = await fetchFileContent(fileName);
      if (content === null || content === undefined) throw new Error(`Nie udało się odczytać pliku ${fileName}`);
      // Hash complete, framed entries so empty files, paths and late edits all count.
      hash.update(JSON.stringify([fileName, content]));
    }
    
    const library = readSongLibraryState(getWorkspaceId());
    hash.update(JSON.stringify({version: 1,
      favorites: library.favorites.slice().sort((a, b) => a.songKey.localeCompare(b.songKey)),
      usage: library.usage.slice().sort((a, b) => a.id.localeCompare(b.id))}));
    return hash.finalize().toString();
  }
  
  /**
   * Compare the current content with the latest backup
   * Returns true if the contents are the same
   */
  static async isContentSameAsLatestBackup() {
    const currentHash = await this.calculateContentHash();
    const backups = await getBackups();
    
    if (backups.length === 0) {
      return false; // No backups exist
    }
    
    const latestBackup = backups[0]; // backups are sorted by date, newest first
    return latestBackup.contentHash === currentHash;
  }
  
  /**
   * Saves a backup of all songs to disk (This method might be redundant now but kept for potential direct download use)
   */
  static async downloadSongsBackup() {
    const { fileName, blob } = await this.createSongsBackup();
    // Use the new helper
    this.downloadBlob(blob, fileName);
  }
  
  /**
   * Stores the backup in IndexedDB for future access if content has changed
   * Returns an object { storedBackup, blob, fileName } if created, null if skipped (no changes)
   */
  static async storeBackup(label = null, {force = false} = {}) {
    // Skip backup if content hasn't changed
    const currentHash = await this.calculateContentHash();
    const backups = await getBackups();
    if (!force && backups.length > 0 && backups[0].contentHash === currentHash) {
        console.log('Backup skipped: Content is the same as the latest backup');
        return null; // Indicate skipped backup
    }
    
    // Create backup data but don't pass contentHash yet, it's calculated inside createSongsBackup now
    const { blob, fileName, fileCount, totalFileSize, zipSize, contentHash } = await this.createSongsBackup(); 
    
    // Convert blob to base64 for storage
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async () => {
        try {
          const base64data = reader.result.split(',')[1];
          const date = new Date();
          
          // Create backup object
          const backupEntry = {
            id: `backup_${date.getTime()}`,
            date: date.toISOString(),
            label: label || `Kopia ${date.toLocaleDateString()} ${date.toLocaleTimeString()}`,
            data: base64data,
            contentHash, // Use the hash calculated during creation
            fileCount,
            totalFileSize,
            zipSize
          };
          
          // Store in IndexedDB and maintain retention policy
          const storedBackup = await storeBackupInDb(backupEntry); // Use renamed import
          await deleteOldBackups(10); // Limit to last 10 backups
          
          // Resolve with stored data AND blob/filename for download
          resolve({ storedBackup, blob, fileName }); 
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Nie udało się odczytać danych'));
      reader.readAsDataURL(blob);
    });
  }
  
  /**
   * Gets all stored backups from IndexedDB
   */
  static async getStoredBackups() {
    return await getBackups();
  }
  
  /**
   * Restores a backup from its ID
   */
  static async restoreBackup(backupId) {
    assertWorkspaceWritable(getWorkspaceId());
    const backup = await getBackup(backupId);
    if (!backup) {
      throw new Error('Nie znaleziono kopii zapasowej');
    }
    
    // Convert base64 to blob
    const blob = this.base64ToBlob(backup.data, 'application/zip');
    
    // Process the zip file and extract songs
    return this.processZippedSongs(blob);
  }
  
  /**
   * Convert base64 to Blob
   */
  static base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    
    return new Blob(byteArrays, { type: mimeType });
  }
  
  /**
   * Delete a backup by ID
   */
  static async deleteBackup(backupId) {
    return await deleteBackup(backupId);
  }
  
  /**
   * Process an uploaded zip file with songs
   */
  static async processZippedSongs(zipFile) {
    const processedFiles = [];
    let manifestError;
    
    await handleZippedTextFiles({ zipFile }, async (entry) => {
      if (entry.relativePath === LIBRARY_MANIFEST) {
        try { processedFiles.libraryState = validateLibraryState(JSON.parse(entry.fileData)); }
        catch (error) { manifestError = error; }
        return;
      }
      if (!/\.txt$/i.test(entry.relativePath)) return;
      processedFiles.push({
        name: entry.relativePath,
        content: entry.fileData
      });
    });
    
    if (manifestError) throw manifestError;
    return processedFiles;
  }

  /**
   * Helper function to trigger browser download for a Blob
   */
  static downloadBlob(blob, fileName) {
    try {
      // Create a temporary download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href); // Clean up the object URL
    } catch (error) {
      console.error('Error triggering download:', error);
      // Optionally alert the user
      alert('Nie udało się automatycznie pobrać pliku kopii zapasowej.');
    }
  }

  /**
   * Register a callback to be notified when backups change
   * Returns a function to unregister the observer
   */
  static observeBackupsChanges(callback) {
    return observeBackupsChanges(callback);
  }
  
}
